import { makeAutoObservable, runInAction } from 'mobx'
import type { DocumentNode } from '@apollo/client'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'

import { playPopupOpenSound } from '../game/audio/uiSounds'
import { getStrapiContent } from '../lib/strapi/strapiUtils'
import { strapiEndpoints } from '../lib/strapi/strapiFetch.config'
import { getQueryRootFieldName } from '../lib/graphql/queryMeta'
import {
  GetLandingPageMappingsDocument,
  type GetLandingPageMappingsQuery,
} from '../lib/graphql/generated/graphql'
import { resolveEntryReferrer } from '../lib/referer/entryReferrer'
import {
  findLandingMappingBySvid,
  findLandingMappingForReferrerHost,
  type LandingpageMappingRow,
} from '../lib/referer/matchLandingpageMapping'
import { getResolvedSvid, parseSvidQuery } from '../utils/svidQuery'
import { hasEffectiveRefId, parseRefIdQuery } from '../utils/refIdQuery'
import { findLandingMappingByDocumentId } from '../utils/parentsLandingBooking'
import type { AppFlowContext, AppFlowEvent, AppFlowPhase } from '../game/state/appFlowFsm'
import { resolveAppFlowTransition } from '../game/state/appFlowFsm'
import { TutorialStore, type TutorialHintGroup } from './TutorialStore'
import { PowerUpTutorialStore } from './PowerUpTutorialStore'
import { rollRandomChameleonCustomization } from '../game/models/powerups/characterCustomizationOptions'
import {
  footstepEffectTypeKeys,
  gearItems,
  type FootstepEffectType,
} from '../game/effects/footstepConfig'
import { gearNames } from '../game/models/character/characterGear'
import type { ParentsInfoBannerBlockId } from '../game/ui/banner/parentsInfoBannerTypes'

export type EntryReferrerLandingMatch = {
  referrerHostname: string
  mapping: NonNullable<GetLandingPageMappingsQuery['landingpageMappings'][number]> | null
}

// Decoded `?cID` stats that drives the card-landing collectible card
export type ParentsCardStats = {
  remainingHearts: number
  coinsCollected: number
  batteryPowerUpsCollected: number
  starPowerUpsCollected: number
  shieldPowerUpsCollected: number
}

export type GameState = 'menu' | 'cameraTranstitionMenuToGame' | 'leavingStartArea' | 'game'
export type RunMode = 'normal' | 'debug'
export type CacheStatus = 'idle' | 'loading' | 'success' | 'error'
export type ConfiguratorTabId = 'color' | 'effect' | 'equipment'
/** Where a run restart drops the player back into the flow. */
export type RunRestartTarget = 'countdown' | 'configurator'

export type RunRestartOptions = {
  withResetTransition?: boolean
}

const displayTrackDistanceMax = 100

// Default character / appearance values used both on init and on reset
const DEFAULT_CHARACTER_COLOR = '#FFFFFF'
const DEFAULT_CHARACTER_ITEM_PRESET_INDEX = 0
const DEFAULT_CHAMELEON_COLOR = '#ffffff'
const DEFAULT_CHAMELEON_TEXTURE = '01'
const DEFAULT_FOOTSTEP_EFFECT: FootstepEffectType = 'dust'
const DEFAULT_UNLOCKED_GEAR_LEVEL = 0
const DEFAULT_SELECTED_DECORATION_ITEMS: string[] = []
const DEFAULT_SELECTED_GEAR_ITEMS: number[] = []
const DEFAULT_DEBUG_FXAA_ENABLED = true
const DEFAULT_DEBUG_FREE_FLY_CAMERA_ENABLED = false
const PARENTS_INFO_BANNER_MIN_DISPLAY_MS = 6_000

type ParentsInfoBannerPriority = number
type ParentsInfoBannerOptions = {
  priority?: ParentsInfoBannerPriority
}

function pickRandom<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)]!
}

function pickRandomSubset<T>(items: readonly T[]): T[] {
  return items.filter(() => Math.random() >= 0.5)
}

class Store {
  appFlowPhase: AppFlowPhase = 'splash'
  runMode: RunMode = 'normal'
  characterColor: string = DEFAULT_CHARACTER_COLOR
  // Placeholder for Items tab; reserved for future loadouts
  characterItemPresetIndex: number = DEFAULT_CHARACTER_ITEM_PRESET_INDEX
  cache: Record<string, unknown> = {}
  cacheStatus: CacheStatus = 'idle'
  cacheError: Error | null = null
  countdownComplete = false
  debugLaneCount = 3
  debugShowObstacleHitboxes = false
  debugInvincible = false

  debugFreeFlyCameraEnabled = DEFAULT_DEBUG_FREE_FLY_CAMERA_ENABLED
  trackDistance = 0
  trackProgress = 0
  settingsOpen = false
  // True while the Stele "Spiel auf deinem Handy weiter" modal is open (hides other in-game UI)
  continueOnPhoneOpen = false
  // Active parents info block shown inside the Stele banner (`intro` or a power-up type)
  parentsInfoBannerBlock: ParentsInfoBannerBlockId | null = null
  parentsInfoBannerPriority: ParentsInfoBannerPriority = 0
  parentsInfoBannerLockUntil = 0
  soundEnabled = true
  tutorialEnabled = true
  vibrationEnabled = true
  tutorial = new TutorialStore()
  powerUpTutorial = new PowerUpTutorialStore()

  parentsCardLandingActive = false

  parentsLandingLevelCompletePending = false

  parentsLandingShowUi = false

  parentsCardStats: ParentsCardStats | null = null

  runRestartPending = false

  // Set when a full game reset is requested; consumed by GameResetBridge inside the scene
  gameResetPending = false

  // transition for overlay when returning to start
  resetTransitionActive = false
  resetTransitionStartedAt = 0

  entryReferrerLandingMatch: EntryReferrerLandingMatch | null = null

  // Editor Settings
  chameleonColor = DEFAULT_CHAMELEON_COLOR
  chameleonTexture = DEFAULT_CHAMELEON_TEXTURE
  footstepEffect: FootstepEffectType = DEFAULT_FOOTSTEP_EFFECT
  configuratorActiveTab: ConfiguratorTabId = 'color'
  unlockedGearLevel = DEFAULT_UNLOCKED_GEAR_LEVEL
  selectedDecorationItems: Set<string> = new Set(DEFAULT_SELECTED_DECORATION_ITEMS)
  selectedGearItems: Set<number> = new Set(DEFAULT_SELECTED_GEAR_ITEMS)


  constructor() {
    makeAutoObservable(this, {}, { autoBind: true })
  }

  setChameleonColor(color: string) {
    this.chameleonColor = color
  }

  setDebugLaneCount(value: number) {
    this.debugLaneCount = value;
  }

  setDebugShowObstacleHitboxes(value: boolean) {
    this.debugShowObstacleHitboxes = value
  }

  setDebugInvincible(value: boolean) {
    this.debugInvincible = value
  }

  setDebugFreeFlyCameraEnabled(value: boolean) {
    this.debugFreeFlyCameraEnabled = value
  }

  setTrackDistance(value: number) {
    this.trackDistance = Math.max(value, 0)
    this.tutorial.syncTrackDistance(this.trackDistance)
  }

  setTrackProgress(value: number) {
    this.trackProgress = Math.min(Math.max(value, 0), 1)
  }

  get displayTrackDistance() {
    return this.trackProgress * displayTrackDistanceMax
  }

  openSettings() {
    playPopupOpenSound()
    this.settingsOpen = true
  }

  closeSettings() {
    this.settingsOpen = false
  }

  setContinueOnPhoneOpen(open: boolean) {
    if (open && !this.continueOnPhoneOpen) {
      playPopupOpenSound()
    }
    this.continueOnPhoneOpen = open
  }

  setParentsInfoBannerBlock(
    block: ParentsInfoBannerBlockId | null,
    options?: ParentsInfoBannerOptions,
  ) {
    if (block === null) {
      this.clearParentsInfoBannerState()
      return
    }

    const priority = options?.priority ?? 0
    const now =
      typeof performance !== 'undefined' ? performance.now() : Date.now()
    const isLocked = now < this.parentsInfoBannerLockUntil

    if (this.parentsInfoBannerBlock === null) {
      this.showParentsInfoBannerBlock(block, priority, now)
      return
    }

    if (isLocked) {
      if (priority > this.parentsInfoBannerPriority && this.parentsInfoBannerBlock !== block) {
        this.showParentsInfoBannerBlock(block, priority, now)
      }
      return
    }

    if (this.parentsInfoBannerBlock === block) {
      return
    }

    this.showParentsInfoBannerBlock(block, priority, now)
  }

  showParentsInfoBannerBlock(
    block: ParentsInfoBannerBlockId,
    priority: ParentsInfoBannerPriority,
    now =
      typeof performance !== 'undefined' ? performance.now() : Date.now(),
  ) {
    this.parentsInfoBannerBlock = block
    this.parentsInfoBannerPriority = priority
    this.parentsInfoBannerLockUntil = now + PARENTS_INFO_BANNER_MIN_DISPLAY_MS
  }

  clearParentsInfoBannerState() {
    this.parentsInfoBannerBlock = null
    this.parentsInfoBannerPriority = 0
    this.parentsInfoBannerLockUntil = 0
  }

  setSoundEnabled(enabled: boolean) {
    this.soundEnabled = enabled
  }

  setTutorialEnabled(enabled: boolean) {
    this.tutorialEnabled = enabled
    if (!enabled) {
      this.tutorial.reset()
      this.powerUpTutorial.reset()
    }
  }

  setVibrationEnabled(enabled: boolean) {
    this.vibrationEnabled = enabled
  }

  isTutorialHintDisplayed(group: TutorialHintGroup) {
    return this.tutorial.isHintDisplayed(group, {
      tutorialEnabled: this.tutorialEnabled,
      trackDistance: this.trackDistance,
    })
  }

  markTutorialPlayed() {
    if (!this.tutorialEnabled) return
    this.tutorialEnabled = false
    this.tutorial.reset()
  }

  setChameleonTexture(texture: string) {
    this.chameleonTexture = texture
  }

  setConfiguratorActiveTab(tabId: ConfiguratorTabId) {
    this.configuratorActiveTab = tabId
  }

  raiseGearLevel(level: number) {
    if (level > this.unlockedGearLevel) {
      this.selectedGearItems.add(level - 1)
      this.unlockedGearLevel = level
    }
  }

  setFootstepEffect(effect: FootstepEffectType) {
    this.footstepEffect = effect
  }

  setUnlockedGearLevel(level: number) {
    this.unlockedGearLevel = Math.max(0, Math.floor(level))
  }

  setSelectedGearItems(indices: Iterable<number>) {
    this.selectedGearItems = new Set(indices)
  }

  setSelectedDecorationItems(items: Iterable<string>) {
    this.selectedDecorationItems = new Set(items)
  }

  setSelectedDecorationItem(item: string) {
    this.selectedDecorationItems = item ? new Set([item]) : new Set()
  }

  toggleDecorationItem(item: string) {
    if (this.selectedDecorationItems.has(item)) {
      this.selectedDecorationItems.delete(item)
      return
    }

    this.selectedDecorationItems.add(item)
  }

  toggleGearItem(index: number) {
    if (index >= this.unlockedGearLevel) return

    if (this.selectedGearItems.has(index)) {
      this.selectedGearItems.delete(index)
      return
    }

    this.selectedGearItems.add(index)
  }

  resetGearState() {
    this.unlockedGearLevel = DEFAULT_UNLOCKED_GEAR_LEVEL
    this.selectedDecorationItems = new Set(DEFAULT_SELECTED_DECORATION_ITEMS)
    this.selectedGearItems = new Set(DEFAULT_SELECTED_GEAR_ITEMS)
  }

  // State Management
  setCurrentGameState(nextState: AppFlowPhase) {
    this.appFlowPhase = nextState
  }

  startGame(mode: RunMode = 'normal') {
    if (this.appFlowPhase !== 'run') return

    this.runMode = mode
  }

  setRunMode(next: RunMode) {
    this.runMode = next
  }

  openEditor() {
    this.appFlowPhase = 'configurator'
    this.runMode = 'normal'
    this.configuratorActiveTab = 'color'
  }

  /**
   * App-shell / pre-game flow. Pass `ctx` from the root view so splash guards
   * can see preload + cache readiness.
   */
  dispatchAppFlow(event: AppFlowEvent, ctx: Omit<AppFlowContext, 'tutorialEnabled'>) {
    const result = resolveAppFlowTransition(this.appFlowPhase, event, {
      ...ctx,
      tutorialEnabled: this.tutorialEnabled,
    })

    if (!result) {
      return
    }

    runInAction(() => {
      if (event.type === 'COUNTDOWN_COMPLETE') {
        this.countdownComplete = true
      }

      if (
        event.type === 'FLOW_ADVANCE' &&
        (this.appFlowPhase === 'intro' ||
          (this.appFlowPhase === 'configurator' && !this.tutorialEnabled))
      ) {
        this.countdownComplete = false
      }

      if (
        event.type === 'RETURN_TO_MENU' ||
        event.type === 'RETURN_TO_MENU_CONFIGURATOR' ||
        event.type === 'DEBUG_RUN' ||
        event.type === 'RESTART_TO_COUNTDOWN'
      ) {
        this.countdownComplete = false
      }

      this.appFlowPhase = result.nextPhase

      for (const effect of result.effects) {
        if (effect === 'enterMenu') {
          this.openEditor()
        } else if (effect === 'startNormal') {
          this.startGame('normal')
        } else if (effect === 'startDebug') {
          this.startGame('debug')
        }
      }
    })
  }

  setCharacterColor(value: string) {
    this.characterColor = value
  }

  setCharacterItemPresetIndex(index: number) {
    this.characterItemPresetIndex = index
  }

  randomizeCharacterCustomization() {
    const roll = rollRandomChameleonCustomization({
      texture: this.chameleonTexture,
    })

    const unlockedGearIndices = gearItems
      .filter(({ gearIndex }) => gearIndex < this.unlockedGearLevel)
      .map(({ gearIndex }) => gearIndex)

    const availableDecorationItems = gearNames.slice(0, 3)

    this.chameleonTexture = roll.texture
    this.footstepEffect = pickRandom(footstepEffectTypeKeys)
    this.selectedDecorationItems = new Set(
      pickRandomSubset(availableDecorationItems),
    )
    this.selectedGearItems = new Set(pickRandomSubset(unlockedGearIndices))
  }

  async initializeCache() {
    if (this.cacheStatus === 'loading' || this.cacheStatus === 'success') {
      return
    }

    this.cacheStatus = 'loading'
    this.cacheError = null

    try {
      const entries = await Promise.all(
        strapiEndpoints.map(async (endpoint) => {
          const data = await getStrapiContent<unknown>(endpoint.key)
          return [endpoint.key, data] as const
        }),
      )

      runInAction(() => {
        this.cache = Object.fromEntries(entries)
        this.cacheStatus = 'success'
      })
    } catch (error) {
      runInAction(() => {
        this.cacheStatus = 'error'
        this.cacheError =
          error instanceof Error ? error : new Error('Failed to fetch data')
        this.entryReferrerLandingMatch = null
      })
    }
  }

  retryCacheInitialization() {
    this.cacheStatus = 'idle'
    this.cacheError = null
    void this.initializeCache()
  }

  getCachedQuery<TData, TVariables = Record<string, unknown>>(
    query: TypedDocumentNode<TData, TVariables> | DocumentNode,
  ): TData | null {
    const key = getQueryRootFieldName(query)
    return (this.cache[key] as TData | undefined) ?? null
  }

  recomputeEntryReferrerLandingMapping() {
    const data = this.getCachedQuery(GetLandingPageMappingsDocument)
    const rows = (data?.landingpageMappings ?? []).filter(
      (r): r is LandingpageMappingRow => r != null,
    )

    const search = typeof window === 'undefined' ? '' : window.location.search

    // if there is a refId in the url, match with strapi and overwrite in the store
    const refIdQuery = parseRefIdQuery(search)
    if (hasEffectiveRefId(refIdQuery)) {
      this.entryReferrerLandingMatch = {
        referrerHostname: '',
        mapping: findLandingMappingByDocumentId(refIdQuery.value.trim(), rows),
      }
      return
    }

    // map svid against Strapi data. Downstream handling (linkouts, qr-codes, generated urls)
    const svid = getResolvedSvid(parseSvidQuery(search))
    if (svid) {
      this.entryReferrerLandingMatch = {
        referrerHostname: '',
        mapping: findLandingMappingBySvid(svid, rows),
      }
      return
    }

    // Non-stele flow: resolve the mapping from the external referer host.
    const ref = resolveEntryReferrer()
    if (!ref) {
      this.entryReferrerLandingMatch = null
      return
    }

    const mapping = findLandingMappingForReferrerHost(ref.hostname, rows)
    this.entryReferrerLandingMatch = {
      referrerHostname: ref.hostname,
      mapping,
    }
  }

  setCountdownComplete(next: boolean) {
    this.countdownComplete = next
  }

  setParentsCardLandingActive(active: boolean) {
    this.parentsCardLandingActive = active
  }

  requestParentsLandingLevelComplete() {
    this.parentsLandingLevelCompletePending = true
  }

  clearParentsLandingLevelCompletePending() {
    this.parentsLandingLevelCompletePending = false
  }

  setParentsLandingShowUi(visible: boolean) {
    this.parentsLandingShowUi = visible
  }

  setParentsCardStats(stats: ParentsCardStats | null) {
    this.parentsCardStats = stats
  }

  requestRunRestart(
    target: RunRestartTarget = 'countdown',
    options?: RunRestartOptions,
  ) {
    this.runRestartPending = true
    if (target !== 'configurator' || options?.withResetTransition) {
      this.beginResetTransition()
    }
    this.dispatchAppFlow(
      target === 'configurator'
        ? { type: 'RETURN_TO_MENU_CONFIGURATOR' }
        : { type: 'RESTART_TO_COUNTDOWN' },
      { canContinueFromSplash: true },
    )
  }

  clearRunRestartPending() {
    this.runRestartPending = false
  }

  resetCharacterCustomization() {
    this.characterColor = DEFAULT_CHARACTER_COLOR
    this.characterItemPresetIndex = DEFAULT_CHARACTER_ITEM_PRESET_INDEX
    this.chameleonColor = DEFAULT_CHAMELEON_COLOR
    this.chameleonTexture = DEFAULT_CHAMELEON_TEXTURE
    this.footstepEffect = DEFAULT_FOOTSTEP_EFFECT
    this.resetGearState()

    this.debugFreeFlyCameraEnabled = DEFAULT_DEBUG_FREE_FLY_CAMERA_ENABLED
  }

  resetGame() {
    this.resetCharacterCustomization()
    this.tutorialEnabled = true
    this.tutorial.reset()
    this.powerUpTutorial.reset()
    this.setTrackDistance(0)
    this.setTrackProgress(0)
    this.countdownComplete = false
    this.runRestartPending = false
    this.settingsOpen = false
    this.continueOnPhoneOpen = false
    this.clearParentsInfoBannerState()
    this.runMode = 'normal'
    this.configuratorActiveTab = 'color'
    this.appFlowPhase = 'start'
    this.gameResetPending = true
    this.beginResetTransition()
  }

  clearGameResetPending() {
    this.gameResetPending = false
  }

  beginResetTransition() {
    this.resetTransitionActive = true
    this.resetTransitionStartedAt =
      typeof performance !== 'undefined' ? performance.now() : Date.now()
  }

  endResetTransition() {
    this.resetTransitionActive = false
  }
}

export const store = new Store()








